import { createContext, useContext, useState, ReactNode } from "react";
import { useEffect } from "react";
import Swal from "sweetalert2";
import { useModal } from "../../../hooks/useModal";
import {
  useUsuarios,
  useCreateUsuario,
  useUpdateUsuario,
  useDeleteUsuario,
} from "../../../hooks/useUsuarios";
import { useGlobalStore } from "../../../store/globalStore";
import {
  UsuarioCreateData,
  UsuarioUpdateData,
  usuarioFilterSchema,
} from "../../../lib/validations";
import { optionsService } from "../../../services/optionsService";
import { useAuth } from "../../../context/AuthContext";
import { isDesenvolvedorRoleNome } from "../../../lib/screenAccess";
import { toast } from "react-toastify";
import { COLORS, MESSAGES } from "../../../constants";

type UsuarioFormData = UsuarioCreateData | UsuarioUpdateData;

interface FilterState {
  nome: string;
  email: string;
  lojaId: string;
  roleId: string;
  ativo: string;
}

interface UsuarioContextType {
  filters: FilterState;
  searchFilters: FilterState;
  filterErrors: Record<string, string>;
  lojas: Array<{ id: number; nome: string }>;
  roles: Array<{ id: number; nome: string }>;
  handleFieldChange: (name: string, value: string) => void;
  handleSearch: () => boolean;
  handleClear: () => void;
  clearFilterErrors: () => void;

  isOpen: boolean;
  editingItem: any;
  openModal: (item?: any) => void;
  closeModal: () => void;

  usuarios: any[];
  isLoading: boolean;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  setCurrentPage: (page: number) => void;
  handleSubmit: (formData: UsuarioFormData) => Promise<void>;
  handleEdit: (rowIndex: number) => void;
  handleDelete: (rowIndex: number) => Promise<void>;
}

const UsuarioContext = createContext<UsuarioContextType | undefined>(undefined);

export const UsuarioProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const isDesenvolvedor = isDesenvolvedorRoleNome(user?.role?.nome);
  const [filters, setFilters] = useState<FilterState>({
    nome: "",
    email: "",
    lojaId: "",
    roleId: "",
    ativo: "true",
  });

  const [searchFilters, setSearchFilters] = useState<FilterState>({
    nome: "",
    email: "",
    lojaId: "",
    roleId: "",
    ativo: "true",
  });

  const [lojas, setLojas] = useState<Array<{ id: number; nome: string }>>([]);
  const [roles, setRoles] = useState<Array<{ id: number; nome: string }>>([]);

  const { isOpen, editingItem, openModal, closeModal } = useModal();
  const { error, clearError } = useGlobalStore();

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [filterErrors, setFilterErrors] = useState<Record<string, string>>({});

  const processedFilters = {
    nome: searchFilters.nome || undefined,
    email: searchFilters.email || undefined,
    lojaId: searchFilters.lojaId ? parseInt(searchFilters.lojaId) : undefined,
    roleId: searchFilters.roleId ? parseInt(searchFilters.roleId) : undefined,
    ativo:
      searchFilters.ativo === "true"
        ? true
        : searchFilters.ativo === "false"
          ? false
          : undefined,
    page: currentPage,
    limit: itemsPerPage,
  };

  const { data: paginatedData, isLoading: isLoadingUsuarios } =
    useUsuarios(processedFilters);
  const usuarios = paginatedData?.data ?? [];
  const totalPages = paginatedData?.totalPages ?? 0;
  const totalItems = paginatedData?.total ?? 0;
  const createMutation = useCreateUsuario();
  const updateMutation = useUpdateUsuario();
  const deleteMutation = useDeleteUsuario();

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const [lojasData, rolesData] = await Promise.all([
          optionsService.getLojas(),
          optionsService.getRoles(undefined, isDesenvolvedor),
        ]);
        setLojas(lojasData || []);
        setRoles(rolesData || []);
      } catch (error) {
        console.error("Erro ao buscar opções:", error);
      }
    };
    fetchOptions();
  }, [isDesenvolvedor]);

  useEffect(() => {
    const allErrors = [
      createMutation.error,
      updateMutation.error,
      deleteMutation.error,
    ].filter(Boolean);

    if (allErrors.length > 0) {
      Swal.fire("Erro Identificado", allErrors[0]?.message, "error");
    }
  }, [createMutation.error, updateMutation.error, deleteMutation.error]);

  useEffect(() => {
    if (error) {
      Swal.fire("Erro Identificado", error, "error");
      clearError();
    }
  }, [error, clearError]);

  const handleFieldChange = (name: string, value: string) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
    if (filterErrors[name]) {
      setFilterErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleSearch = (): boolean => {
    setFilterErrors({});
    const result = usuarioFilterSchema.safeParse({
      nome: filters.nome,
      email: filters.email,
      lojaId: filters.lojaId,
      roleId: filters.roleId,
      ativo: filters.ativo,
    });

    if (!result.success) {
      const flattened = result.error.flatten();
      const fieldErrors: Record<string, string> = {};
      Object.entries(flattened.fieldErrors).forEach(([key, messages]) => {
        const msg = Array.isArray(messages) ? messages[0] : messages;
        if (msg) fieldErrors[key] = msg;
      });
      setFilterErrors(fieldErrors);
      return false;
    }

    setSearchFilters(filters);
    setCurrentPage(1);
    return true;
  };

  const clearFilterErrors = () => setFilterErrors({});

  const handleClear = () => {
    setFilters({ nome: "", email: "", lojaId: "", roleId: "", ativo: "true" });
    setSearchFilters({
      nome: "",
      email: "",
      lojaId: "",
      roleId: "",
      ativo: "true",
    });
    setCurrentPage(1);
  };

  const handleSubmit = async (formData: UsuarioFormData) => {
    try {
      if (editingItem) {
        const updateData = { ...formData } as any;
        if (!updateData.senha || updateData.senha.trim() === "") {
          delete updateData.senha;
        }
        await updateMutation.mutateAsync({
          id: editingItem.id,
          data: updateData,
        });
        toast.success(MESSAGES.UPDATE);
      } else {
        await createMutation.mutateAsync(formData as UsuarioCreateData);
        toast.success(MESSAGES.CREATE);
      }
      closeModal();
    } catch (error) {
      toast.error("Erro ao salvar usuário: " + error);
    }
  };

  const handleEdit = (rowIndex: number) => {
    openModal(usuarios[rowIndex]);
  };

  const handleDelete = async (rowIndex: number) => {
    const result = await Swal.fire({
      title: "Tem certeza?",
      text: "Esta ação não pode ser desfeita",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: COLORS.PRIMARY,
      cancelButtonColor: COLORS.NEUTRAL_500,
      confirmButtonText: "Sim, deletar!",
      cancelButtonText: "Cancelar",
    });

    if (result.isConfirmed) {
      try {
        await deleteMutation.mutateAsync(usuarios[rowIndex].id);
        Swal.fire({
          title: "Deletado!",
          text: "O usuário foi removido com sucesso.",
          icon: "success",
          confirmButtonColor: COLORS.PRIMARY,
        });
      } catch (error) {
        console.error("Erro ao deletar usuário:", error);
      }
    }
  };

  return (
    <UsuarioContext.Provider
      value={{
        filters,
        searchFilters,
        filterErrors,
        lojas,
        roles,
        handleFieldChange,
        handleSearch,
        handleClear,
        clearFilterErrors,
        isOpen,
        editingItem,
        openModal,
        closeModal,
        usuarios,
        isLoading:
          isLoadingUsuarios ||
          createMutation.isPending ||
          updateMutation.isPending,
        currentPage,
        totalPages,
        totalItems,
        itemsPerPage,
        setCurrentPage,
        handleSubmit,
        handleEdit,
        handleDelete,
      }}
    >
      {children}
    </UsuarioContext.Provider>
  );
};

export const useUsuario = () => {
  const context = useContext(UsuarioContext);
  if (!context) {
    throw new Error("useUsuario must be used within UsuarioProvider");
  }
  return context;
};
